// frontend/src/pages/Reinsurance/components/common/ReinsuranceTable/index.js
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSort, faSortUp, faSortDown, 
  faChevronLeft, faChevronRight 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import styles from './ReinsuranceTable.module.css';

const ReinsuranceTable = ({ 
  columns, 
  data, 
  onRowClick, 
  emptyMessage = 'No data available',
  sortConfig = null,
  onSort = null,
  isLoading = false,
  pageSize = 3 // Smaller page size to ensure pagination shows with small data sets
}) => {
  const { t } = useLanguage();
  // State to track column widths
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null);
  const tableRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedData, setPaginatedData] = useState([]);
  
  // Initialize column widths on first render
  useEffect(() => {
    const initialWidths = {};
    columns.forEach(column => {
      // Set default widths based on column type
      if (column.id === 'contract_number') {
        initialWidths[column.id] = 200; // Fixed width for identifiers
      } else if (column.id === 'rnr') {
        initialWidths[column.id] = 150; // More space for names
      } else if (column.id === 'cedent_name') {
        initialWidths[column.id] = 200; // More space for names
      } else if (column.id === 'status') {
        initialWidths[column.id] = 120; // Status badges
      } else if (column.format === 'date') {
        initialWidths[column.id] = 200; // Dates
      } else if (column.format === 'currency') {
        initialWidths[column.id] = 200; // Currency values
      } else {
        initialWidths[column.id] = 150; // Default width
      }
    });
    setColumnWidths(initialWidths);
    
    // Calculate total table width to ensure proper scrolling
    const totalWidth = Object.values(initialWidths).reduce((sum, width) => sum + width, 0);
    if (tableRef.current) {
      tableRef.current.style.minWidth = `${totalWidth}px`;
    }
  }, [columns]);
  
  // Update pagination when data changes
  useEffect(() => {
    if (data && data.length > 0) {
      const pages = Math.ceil(data.length / pageSize);
      setTotalPages(pages);
      
      // Reset to first page if current page is out of bounds
      if (currentPage > pages) {
        setCurrentPage(1);
      }
      
      // Slice data for current page
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setPaginatedData(data.slice(startIndex, endIndex));
    } else {
      setPaginatedData([]);
      setTotalPages(1);
      setCurrentPage(1);
    }
  }, [data, pageSize, currentPage]);

  // Helper function to format cell values
  const formatCellValue = (row, column) => {
    let value;
    
    // Use accessor function if provided
    if (column.accessor) {
      value = column.accessor(row);
    } else {
      value = row[column.id];
    }
    
    // Apply formatting if specified
    if (column.format === 'date' && value) {
      return new Date(value).toLocaleDateString();
    } else if (column.format === 'currency' && value != null) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    } else if (column.format === 'percentage' && value != null) {
      return `${value}%`;
    }
    
    return value || '-';
  };
  
  // Get sort direction icon
  const getSortIcon = (columnId) => {
    if (!sortConfig) return <FontAwesomeIcon icon={faSort} className={styles.sortIcon} />;
    
    if (sortConfig.key === columnId) {
      return sortConfig.direction === 'ascending' 
        ? <FontAwesomeIcon icon={faSortUp} className={styles.sortIcon} />
        : <FontAwesomeIcon icon={faSortDown} className={styles.sortIcon} />;
    }
    
    return <FontAwesomeIcon icon={faSort} className={styles.sortIcon} />;
  };
  
  // Handle sort
  const handleSort = (columnId) => {
    if (!onSort) return;
    
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === columnId && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    onSort(columnId, direction);
  };
  
  // Handle column resize start
  const handleResizeStart = (e, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(columnId);
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnId] || 150;
    
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  // Handle column resize move
  const handleResizeMove = (e) => {
    if (!resizing) return;
    
    const deltaX = e.clientX - startXRef.current;
    const newWidth = Math.max(80, startWidthRef.current + deltaX); // Minimum width of 80px
    
    setColumnWidths(prev => ({
      ...prev,
      [resizing]: newWidth
    }));
    
    // Recalculate total width and update table min-width
    if (tableRef.current) {
      const updatedWidths = { ...columnWidths, [resizing]: newWidth };
      const totalWidth = Object.values(updatedWidths).reduce((sum, width) => sum + width, 0);
      tableRef.current.style.minWidth = `${totalWidth}px`;
    }
  };
  
  // Handle column resize end
  const handleResizeEnd = () => {
    setResizing(null);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };
  
  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Generate pagination numbers with ellipsis for large page counts
  const getPaginationNumbers = () => {
    const pages = [];
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Show ellipsis or additional pages
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Show ellipsis or additional pages
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);
  
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t('loading_data')}</p>
      </div>
    );
  }
  
  if (!data || data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>{t(emptyMessage)}</p>
      </div>
    );
  }
  
  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper} ref={tableWrapperRef}>
        <table className={styles.table} ref={tableRef}>
          <thead>
            <tr>
              {columns.map(column => (
                <th 
                  key={column.id}
                  className={`${column.sortable !== false && onSort ? styles.sortable : ''} ${resizing === column.id ? styles.resizing : ''}`}
                  style={{ width: columnWidths[column.id] ? `${columnWidths[column.id]}px` : 'auto' }}
                >
                  <div className={styles.thContent}>
                    <div 
                      className={styles.thLabel}
                      onClick={() => column.sortable !== false && onSort ? handleSort(column.id) : null}
                    >
                      {t(column.label)}
                      {column.sortable !== false && onSort && getSortIcon(column.id)}
                    </div>
                    <div 
                      className={styles.resizeHandle}
                      onMouseDown={(e) => handleResizeStart(e, column.id)}
                      title={t('resize_column')}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr 
                key={row.id || rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                className={onRowClick ? styles.clickable : ''}
              >
                {columns.map(column => {
                  const cellValue = formatCellValue(row, column);
                  
                  return (
                    <td 
                      key={`${rowIndex}-${column.id}`}
                      style={{ width: columnWidths[column.id] ? `${columnWidths[column.id]}px` : 'auto' }}
                      className={column.id === 'status' ? styles.statusCell : ''}
                    >
                      {column.id === 'status' ? (
                        <span className={`${styles.statusBadge} ${styles[String(cellValue).toLowerCase()]}`}>
                          {t(String(cellValue).toLowerCase())}
                        </span>
                      ) : (
                        cellValue
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button 
            className={styles.paginationArrow}
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            aria-label={t('previous_page')}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          {getPaginationNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className={styles.ellipsis}>...</span>
            ) : (
              <button
                key={page}
                className={`${styles.pageNumber} ${currentPage === page ? styles.activePage : ''}`}
                onClick={() => handlePageChange(page)}
                aria-label={t('go_to_page', { page })}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            )
          ))}
          
          <button 
            className={styles.paginationArrow}
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            aria-label={t('next_page')}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
      
      <div className={styles.tableInfo}>
        {data.length > 0 && (
          <p>
            {t('showing_items', {
              start: (currentPage - 1) * pageSize + 1,
              end: Math.min(currentPage * pageSize, data.length),
              total: data.length
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReinsuranceTable;